import React, { useEffect, useState } from 'react';
import { apiClient } from '../../../services/apiClient';
import { toastError, toastSuccess } from '../../../lib/toast';

interface Props {
  clientId: string;
  domainCode: string;
  disabled?: boolean;
}

interface ProviderRead {
  provider: string;
  api_key_masked: string;
  has_extra_fields: boolean;
}

interface ApiCredentialsRead {
  providers: ProviderRead[];
}

const KNOWN_PROVIDERS = ['openai', 'anthropic', 'azure', 'mistral', 'gemini'] as const;

export const ApiCredentialsPanel: React.FC<Props> = ({ clientId, domainCode, disabled }) => {
  const [creds, setCreds] = useState<ApiCredentialsRead | null>(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newProvider, setNewProvider] = useState<string>('openai');
  const [newApiKey, setNewApiKey] = useState<string>('');

  const baseUrl = `/admin/clients/${clientId}/active-configs/${domainCode}/api-credentials`;

  const reload = async () => {
    setLoading(true);
    try {
      const r = await apiClient.get<ApiCredentialsRead>(baseUrl);
      setCreds(r.data);
    } catch (err) {
      toastError('Chargement des clés API échoué', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && creds === null) {
      reload();
    }
  }, [open]);

  const handleAdd = async () => {
    if (!newApiKey.trim()) {
      toastError('Clé API requise');
      return;
    }
    setAdding(true);
    try {
      const r = await apiClient.put<ApiCredentialsRead>(baseUrl, {
        provider: newProvider,
        api_key: newApiKey.trim(),
      });
      setCreds(r.data);
      setNewApiKey('');
      toastSuccess(`Clé ${newProvider} enregistrée`);
    } catch (err) {
      toastError('Enregistrement clé échoué', err);
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (provider: string) => {
    if (!window.confirm(`Supprimer la clé ${provider} ? Le système retombera sur la variable d'environnement.`)) {
      return;
    }
    try {
      await apiClient.delete(`${baseUrl}/${provider}`);
      toastSuccess(`Clé ${provider} supprimée`);
      await reload();
    } catch (err) {
      toastError('Suppression échouée', err);
    }
  };

  const providersCount = creds?.providers.length ?? 0;

  return (
    <div className="mt-3 border-t border-gray-200 pt-3">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={disabled}
        className="text-xs text-gray-600 hover:text-gray-900 flex items-center gap-1 disabled:opacity-50"
      >
        <span>{open ? '▼' : '▶'}</span>
        <span>Clés API LLM</span>
        {providersCount > 0 && (
          <span className="ml-1 text-[10px] px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded border border-blue-200">
            {providersCount} configurée{providersCount > 1 ? 's' : ''}
          </span>
        )}
        {open && providersCount === 0 && !loading && (
          <span className="ml-1 text-[10px] text-gray-400 italic">fallback env var</span>
        )}
      </button>

      {open && (
        <div className="mt-2 pl-4 space-y-2 text-xs">
          {loading && <div className="text-gray-500 italic">Chargement…</div>}

          {!loading && creds && creds.providers.length > 0 && (
            <div className="space-y-1">
              {creds.providers.map((p) => (
                <div key={p.provider} className="flex items-center gap-2">
                  <span className="font-mono text-gray-900 min-w-[80px]">{p.provider}</span>
                  <span className="font-mono text-gray-500">{p.api_key_masked}</span>
                  {p.has_extra_fields && (
                    <span className="text-[10px] text-gray-400">(+ extras)</span>
                  )}
                  <button
                    type="button"
                    onClick={() => handleDelete(p.provider)}
                    disabled={disabled}
                    className="ml-auto text-red-600 hover:text-red-800 text-[11px] disabled:opacity-50"
                  >
                    Supprimer
                  </button>
                </div>
              ))}
            </div>
          )}

          {!loading && (
            <div className="flex items-center gap-2 pt-2 border-t border-gray-100">
              <select
                value={newProvider}
                onChange={(e) => setNewProvider(e.target.value)}
                disabled={disabled || adding}
                className="border border-gray-300 rounded px-2 py-1 text-xs"
              >
                {KNOWN_PROVIDERS.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
              <input
                type="password"
                value={newApiKey}
                onChange={(e) => setNewApiKey(e.target.value)}
                placeholder="sk-… (clé API)"
                disabled={disabled || adding}
                className="flex-1 border border-gray-300 rounded px-2 py-1 text-xs font-mono"
              />
              <button
                type="button"
                onClick={handleAdd}
                disabled={disabled || adding || !newApiKey.trim()}
                className="px-2 py-1 text-xs border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50"
              >
                {adding ? '…' : 'Enregistrer'}
              </button>
            </div>
          )}

          <p className="text-[11px] text-gray-500 italic">
            Sans clé custom, Dokimo utilise <code>OPENAI_API_KEY</code> / <code>ANTHROPIC_API_KEY</code> de l'environnement.
            La saisie chiffre la clé (Fernet) ; elle n'est jamais ré-exposée en clair.
          </p>
        </div>
      )}
    </div>
  );
};

export default ApiCredentialsPanel;
